// app/(tabs)/market/index.tsx
//
// Écran par défaut de la section Market : feed de produits, toutes
// boutiques confondues. Branché sur le vrai backend via useMarketFeed
// (POST /market, action get-feed — voir backend/handleMarket.ts).
// Chaque carte produit a des boutons d'action INDÉPENDANTS (chacun son
// propre TouchableOpacity, pour ne jamais déclencher un autre bouton
// par erreur) :
//   - WhatsApp        → ouvre wa.me/{numéro} avec message pré-rempli
//                        (affiché seulement si show_whatsapp_business_button)
//   - Contacter        → messagerie interne, si pas de WhatsApp Business
//                        configuré et show_platform_chat_button actif
//   - Acheter          → ouvre buy_url du produit (show_buy_button)
//   - En savoir plus   → ouvre learn_more_url (show_learn_more_button)
//   - Appeler          → ouvre tel:{call_phone} (show_call_button)
//   - Voir plus         → détail complet du produit
//   - Aller en boutique → page complète de la boutique
//
// PATCH V12 : product.name → product.product_name (voir useMarketFeed.ts,
// patch V12, et handleMarket.ts — la vue market_marketplace_feed expose
// le nom du produit sous product_name, name étant déjà pris par le nom
// de la boutique).
//
// PATCH V13 : la vignette produit était un placeholder statique (View +
// icône Ionicons), jamais reliée à product.cover_image_url. Ajout d'un
// vrai <Image> par carte, avec repli sur le placeholder Ionicons
// d'origine si l'URL est absente ou si le chargement échoue (onError).
// L'état d'échec est suivi par product_id (Set), pour ne pas re-tenter
// indéfiniment une image cassée au re-render.
//
// PATCH V16 : ajout des boutons "En savoir plus" (show_learn_more_button
// / learn_more_url) et "Appeler" (show_call_button / call_phone),
// absents jusqu'ici alors que ces champs existent sur MarketFeedProduct
// et sont bien remontés par le backend. Placés dans une ligne d'actions
// dédiée (conditionnelle, comme la ligne WhatsApp/Contacter/Acheter),
// pour ne pas surcharger une même rangée avec jusqu'à 5 boutons sur une
// carte de largeur compacte.
//
// PATCH V17 : ajout d'une barre de recherche FIXE (toujours visible, pas
// d'icône à déplier) sous l'en-tête, avec un filtre de catégorie optionnel
// (CategoryChips, allowClear=true) affiché uniquement quand une recherche
// est active. Dès que searchTerm.trim() n'est pas vide, le feed normal
// (useMarketFeed) disparaît complètement et cède la place aux résultats de
// useSearchProducts (action backend search-products — name obligatoire,
// category_id optionnel) — jamais les deux affichés en même temps. Vider
// le champ de recherche (ou taper sur la croix de SearchBar) fait
// réapparaître le feed normal exactement où il en était (useMarketFeed
// garde son propre état, il n'est jamais démonté). La catégorie
// sélectionnée dans les chips se combine à searchTerm dans le même appel
// search-products (voir useMarketSearch.ts) — elle est réinitialisée à
// chaque fois que searchTerm redevient vide, pour ne pas la retrouver
// "collée" au prochain terme de recherche sans que la personne l'ait
// choisie à nouveau. Le rendu de carte produit (JSX + styles) est
// inchangé et désormais factorisé dans renderProductCard pour être
// partagé entre le feed normal et les résultats de recherche.
//
// PATCH V18 : en-tête refondu en une seule ligne compacte (icône réduite
// + nombre de produits + barre de recherche), à la place du bloc vertical
// précédent (grosse icône 32px, titre "Marché", sous-texte en dessous).
// Le titre "Marché" est supprimé. L'icône passe de 32px à 18px et se
// place à gauche de la ligne. Le nombre de produits ("X produits
// disponibles") se déplace au milieu de cette même ligne, et reste
// affiché UNIQUEMENT en mode feed normal (masqué pendant une recherche
// active) — comportement identique à avant, simplement repositionné.
// CategoryChips reste sous cette ligne, inchangée, affichée seulement en
// mode recherche. En mode recherche, le nombre affiché est
// searchResults.length (nombre de résultats chargés jusqu'ici) : c'est
// ce que useSearchProducts (useMarketSearch.ts) expose aujourd'hui — ce
// hook ne remonte pas le total réel renvoyé par le backend, seulement
// products/loading/loadingMore/error/hasMore/loadMore.
//
// PATCH V19 : ajustement du schéma [icône][nombre][barre de recherche --->]
// sur une seule ligne collée en haut de l'écran :
//   - La ligne d'en-tête remonte au plus près du coin de l'écran : le
//     paddingTop du scroll et le paddingVertical/marginBottom du header
//     sont réduits au minimum.
//   - La SearchBar utilise désormais sa nouvelle prop `compact` (voir
//     SearchBar.tsx) : hauteur ramenée à 28px, proche de la hauteur de
//     l'icône storefront (18px) — au lieu des 44px par défaut.
//   - Elle démarre juste après le nombre de produits (gap réduit, plus
//     de marginLeft: 'auto' qui créait un grand vide) et s'étend en
//     largeur jusqu'au bord droit de l'écran (flex: 1, à la place de la
//     largeur fixe 168 précédente).
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.index / market.common). Le message
// WhatsApp pré-rempli est également traduit (interpolation {{product}}).

import React, { useState, useRef, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  Animated,
  ActivityIndicator,
  RefreshControl,
  Linking,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';
import { useMarketFeed, type MarketFeedProduct } from '@/hooks/market/useMarketFeed';
import { useContactSeller } from '@/hooks/market/useContactSeller';
import { useFavorites } from '@/hooks/market/useFavorites';
import { useSearchProducts } from '@/hooks/market/useMarketSearch';
import SearchBar from '@/components/market/SearchBar';
import CategoryChips from '@/components/market/CategoryChips';

const NATIVE = Platform.OS !== 'web';

function formatPrice(amount: number, currency: string) {
  return `${amount.toLocaleString('fr-FR')} ${currency}`;
}

export default function MarketFeedScreen() {
  const router = useRouter();
  const { t } = useTranslation();

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastTap, setLastTap] = useState<number | null>(null);
  const headerAnim = useRef(new Animated.Value(0)).current;

  // ── Recherche (PATCH V17) ──
  // searchTerm vide → feed normal (useMarketFeed) affiché comme avant.
  // searchTerm non vide → useSearchProducts prend le relais, combiné à
  // categoryFilter (optionnel, choisi via les chips).
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const isSearching = searchTerm.trim().length > 0;

  const handleSearchChange = useCallback((text: string) => {
    setSearchTerm(text);
    // Si la recherche est vidée, on oublie le filtre catégorie choisi —
    // il ne doit pas rester "collé" à un prochain terme de recherche sans
    // que la personne l'ait choisi à nouveau.
    if (!text.trim()) setCategoryFilter(undefined);
  }, []);

  // Suivi des images de couverture ayant échoué au chargement, par
  // product_id — pour basculer ces cartes précises en placeholder sans
  // affecter les autres. Partagé entre le feed normal et les résultats de
  // recherche (les deux affichent des MarketFeedProduct de même forme).
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const markImageFailed = useCallback((productId: string) => {
    setFailedImages(prev => {
      if (prev.has(productId)) return prev;
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  }, []);

  const { setTabBarHidden } = useTabBarVisibility();

  const {
    products,
    loading,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
  } = useMarketFeed();

  const {
    products: searchResults,
    loading: searchLoading,
    loadingMore: searchLoadingMore,
    error: searchError,
    hasMore: searchHasMore,
    loadMore: searchLoadMore,
  } = useSearchProducts(searchTerm, categoryFilter);

  const { contactSeller, loading: contactLoading } = useContactSeller();
  const { isFavorite, toggleFavorite, togglingId: togglingFavoriteId } = useFavorites();

  const showHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (!prevVisible) {
        Animated.spring(headerAnim, {
          toValue: 0,
          useNativeDriver: true,
          tension: 80,
          friction: 10,
        }).start();
      }
      return true;
    });
  }, [headerAnim]);

  const hideHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (prevVisible) {
        Animated.spring(headerAnim, {
          toValue: -100,
          useNativeDriver: true,
          tension: 80,
          friction: 10,
        }).start();
      }
      return false;
    });
  }, [headerAnim]);

  const { handleScroll } = useScrollAutoHide({
    onShow: useCallback(() => {
      showHeader();
      setTabBarHidden(false);
    }, [showHeader, setTabBarHidden]),
    onHide: useCallback(() => {
      hideHeader();
      setTabBarHidden(true);
    }, [hideHeader, setTabBarHidden]),
    onScrollExtra: useCallback((e: any) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const ratio = (contentOffset.y + layoutMeasurement.height) / contentSize.height;
      if (ratio > 0.85) {
        // Pagine la liste actuellement affichée (recherche ou feed
        // normal), jamais les deux en même temps.
        if (isSearching) {
          if (searchHasMore && !searchLoadingMore) searchLoadMore();
        } else if (hasMore && !loadingMore) {
          loadMore();
        }
      }
    }, [isSearching, searchHasMore, searchLoadingMore, searchLoadMore, hasMore, loadingMore, loadMore]),
  });

  const toggleHeader = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (headerVisible) {
      hideHeader();
      setTabBarHidden(true);
    } else {
      showHeader();
      setTabBarHidden(false);
    }
  }, [headerVisible, hideHeader, showHeader, setTabBarHidden]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleHeader();
    else setLastTap(now);
  }, [lastTap, toggleHeader]);

  // ── Actions par bouton (chacune indépendante, son propre TouchableOpacity) ──

  const handleWhatsApp = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const phone = product.whatsapp_business_phone_override;
    if (!phone) return;
    // Format wa.me attend un numéro sans espaces ni "+" (les chiffres
    // seuls suffisent ; wa.me tolère aussi le "+" mais on nettoie par
    // sécurité).
    const cleanPhone = phone.replace(/[^\d]/g, '');
    const message = encodeURIComponent(t('market.index.whatsappMessage', { product: product.product_name }));
    Linking.openURL(`https://wa.me/${cleanPhone}?text=${message}`);
  }, [t]);

  // Messagerie interne — affichée seulement quand WhatsApp Business
  // n'est PAS configuré pour ce produit et que show_platform_chat_button
  // est actif (réglage choisi par le vendeur). Réutilise le système de
  // conversation privée existant (getOrCreateConversation), pas un
  // système de messagerie séparé pour Market.
  const handleContactSeller = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    contactSeller({
      id:     product.owner_id,
      nom:    product.owner_nom,
      prenom: product.owner_prenom,
    });
  }, [contactSeller]);

  const handleBuy = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (!product.buy_url) return;
    Linking.openURL(product.buy_url);
  }, []);

  const handleLearnMore = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (!product.learn_more_url) return;
    Linking.openURL(product.learn_more_url);
  }, []);

  const handleCall = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (!product.call_phone) return;
    Linking.openURL(`tel:${product.call_phone}`);
  }, []);

  const handleSeeMore = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/produits/${product.product_id}`);
  }, [router]);

  const handleGoToShop = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/boutiques/${product.shop_slug}`);
  }, [router]);

  const handleToggleFavorite = useCallback((product: MarketFeedProduct) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    toggleFavorite(product.product_id);
  }, [toggleFavorite]);

  // ── Choix de la liste à afficher (recherche ou feed normal) ──
  const displayedProducts    = isSearching ? searchResults     : products;
  const displayedLoading     = isSearching ? searchLoading     : loading;
  const displayedLoadingMore = isSearching ? searchLoadingMore : loadingMore;
  const displayedError       = isSearching ? searchError       : error;

  // Rendu d'une carte produit — factorisé (PATCH V17) pour être utilisé
  // identiquement par le feed normal et par les résultats de recherche.
  // Contenu JSX strictement identique à la version précédente, seulement
  // extrait de la boucle .map() inline.
  const renderProductCard = (product: MarketFeedProduct) => {
    const showCover = !!product.cover_image_url && !failedImages.has(product.product_id);

    return (
      <View key={product.product_id} style={styles.productCard}>
        <View style={styles.productTopRow}>
          {showCover ? (
            <View style={styles.productImageWrap}>
              <Image
                source={{ uri: product.cover_image_url }}
                style={styles.productImage}
                resizeMode="cover"
                onError={() => markImageFailed(product.product_id)}
              />
              {product.is_featured && (
                <View style={styles.featuredBadge}>
                  <Text style={styles.featuredBadgeText}>{t('market.index.newBadge')}</Text>
                </View>
              )}
              <TouchableOpacity
                style={styles.favoriteBtn}
                activeOpacity={0.7}
                onPress={() => handleToggleFavorite(product)}
                disabled={togglingFavoriteId === product.product_id}
              >
                <Ionicons
                  name={isFavorite(product.product_id) ? 'heart' : 'heart-outline'}
                  size={16}
                  color={isFavorite(product.product_id) ? '#EF4444' : '#999'}
                />
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.productImagePlaceholder}>
              <Ionicons name="image-outline" size={28} color="#C4B5FD" />
              {product.is_featured && (
                <View style={styles.featuredBadge}>
                  <Text style={styles.featuredBadgeText}>{t('market.index.newBadge')}</Text>
                </View>
              )}
              <TouchableOpacity
                style={styles.favoriteBtn}
                activeOpacity={0.7}
                onPress={() => handleToggleFavorite(product)}
                disabled={togglingFavoriteId === product.product_id}
              >
                <Ionicons
                  name={isFavorite(product.product_id) ? 'heart' : 'heart-outline'}
                  size={16}
                  color={isFavorite(product.product_id) ? '#EF4444' : '#999'}
                />
              </TouchableOpacity>
            </View>
          )}
          <View style={styles.productInfo}>
            <Text style={styles.productName} numberOfLines={1}>{product.product_name}</Text>
            <Text style={styles.productShop} numberOfLines={1}>{product.shop_name}</Text>
            <View style={styles.priceRow}>
              {product.promo_price ? (
                <>
                  <Text style={styles.promoPrice}>{formatPrice(product.promo_price, product.currency_code)}</Text>
                  <Text style={styles.originalPrice}>{formatPrice(product.price, product.currency_code)}</Text>
                </>
              ) : (
                <Text style={styles.price}>{formatPrice(product.price, product.currency_code)}</Text>
              )}
            </View>
          </View>
        </View>

        {/* Ligne 1 : contact / achat — affichés seulement si
            configurés côté boutique/produit. WhatsApp Business est
            prioritaire ; si non configuré, la messagerie interne
            prend le relais (selon ce que le vendeur a choisi). */}
        {(product.show_whatsapp_business_button || product.show_platform_chat_button || product.show_buy_button) && (
          <View style={styles.actionsRow}>
            {product.show_whatsapp_business_button ? (
              <TouchableOpacity
                style={styles.actionBtn}
                activeOpacity={0.7}
                onPress={() => handleWhatsApp(product)}
              >
                <Ionicons name="logo-whatsapp" size={14} color="#25D366" />
                <Text style={styles.actionBtnText}>{t('market.index.whatsapp')}</Text>
              </TouchableOpacity>
            ) : product.show_platform_chat_button ? (
              <TouchableOpacity
                style={styles.actionBtn}
                activeOpacity={0.7}
                onPress={() => handleContactSeller(product)}
                disabled={contactLoading}
              >
                <Ionicons name="chatbubble-ellipses-outline" size={14} color="#6610B1" />
                <Text style={styles.actionBtnText}>
                  {contactLoading ? t('market.index.contacting') : t('market.index.contact')}
                </Text>
              </TouchableOpacity>
            ) : null}
            {product.show_buy_button && (
              <TouchableOpacity
                style={styles.actionBtn}
                activeOpacity={0.7}
                onPress={() => handleBuy(product)}
              >
                <Ionicons name="cart-outline" size={14} color="#6610B1" />
                <Text style={styles.actionBtnText}>{t('market.index.buy')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Ligne 1bis : en savoir plus / appeler — même logique
            conditionnelle que la ligne 1, séparée pour ne pas
            surcharger une même rangée avec jusqu'à 5 boutons sur
            une carte de largeur compacte. */}
        {(product.show_learn_more_button || product.show_call_button) && (
          <View style={styles.actionsRow}>
            {product.show_learn_more_button && (
              <TouchableOpacity
                style={styles.actionBtn}
                activeOpacity={0.7}
                onPress={() => handleLearnMore(product)}
              >
                <Ionicons name="information-circle-outline" size={14} color="#6610B1" />
                <Text style={styles.actionBtnText}>{t('market.index.learnMore')}</Text>
              </TouchableOpacity>
            )}
            {product.show_call_button && (
              <TouchableOpacity
                style={styles.actionBtn}
                activeOpacity={0.7}
                onPress={() => handleCall(product)}
              >
                <Ionicons name="call-outline" size={14} color="#6610B1" />
                <Text style={styles.actionBtnText}>{t('market.index.call')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Ligne 2 : détail produit / boutique — zones de tap
            totalement indépendantes de la ligne 1 ci-dessus. */}
        <View style={styles.actionsRow}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.actionBtnSecondary]}
            activeOpacity={0.7}
            onPress={() => handleSeeMore(product)}
          >
            <Ionicons name="information-circle-outline" size={14} color="#6610B1" />
            <Text style={styles.actionBtnText}>{t('market.index.seeMore')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionBtn, styles.actionBtnSecondary]}
            activeOpacity={0.7}
            onPress={() => handleGoToShop(product)}
          >
            <Ionicons name="storefront-outline" size={14} color="#6610B1" />
            <Text style={styles.actionBtnText}>{t('market.index.goToShop')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor="#6610B1" />
        }
      >
        {/* ── En-tête compact (PATCH V18) : icône + nombre + recherche,
            tous sur une seule ligne. Titre "Marché" supprimé. Le nombre
            de produits n'est affiché qu'en mode feed normal (masqué
            pendant une recherche active), comme avant. */}
        <View style={styles.headerBlock}>
          <Ionicons name="storefront-outline" size={18} color="#6610B1" />

          {!displayedLoading && !displayedError && !isSearching && (
            <Text style={styles.headerCount} numberOfLines={1}>
              {t('market.index.headerCount', { count: products.length })}
            </Text>
          )}

          <View style={styles.headerSearchWrap}>
            <SearchBar
              value={searchTerm}
              onChangeText={handleSearchChange}
              placeholder={t('market.index.searchPlaceholder')}
              compact
            />
          </View>
        </View>

        {isSearching && (
          <View style={styles.categoryFilterWrap}>
            <CategoryChips
              value={categoryFilter}
              onValueChange={setCategoryFilter}
              allowClear
            />
          </View>
        )}

        {/* ── Chargement initial ── */}
        {displayedLoading && (
          <View style={styles.centerBox}>
            <ActivityIndicator size="small" color="#6610B1" />
            <Text style={styles.centerBoxText}>
              {isSearching ? t('market.index.loadingSearch') : t('market.index.loadingNormal')}
            </Text>
          </View>
        )}

        {/* ── Erreur ── */}
        {!displayedLoading && displayedError && (
          <View style={styles.centerBox}>
            <Ionicons name="cloud-offline-outline" size={40} color="#C4B5FD" />
            <Text style={styles.centerBoxText}>
              {displayedError === 'session_expired'
                ? t('market.common.sessionExpiredReconnect')
                : isSearching
                  ? t('market.index.errorSearch')
                  : t('market.index.errorNormal')}
            </Text>
            {!isSearching && (
              <TouchableOpacity style={styles.retryBtn} onPress={refresh} activeOpacity={0.8}>
                <Text style={styles.retryBtnText}>{t('market.common.retry')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* ── Liste vide ── */}
        {!displayedLoading && !displayedError && displayedProducts.length === 0 && (
          <View style={styles.centerBox}>
            <Ionicons name="storefront-outline" size={40} color="#C4B5FD" />
            <Text style={styles.centerBoxText}>
              {isSearching ? t('market.index.emptySearch') : t('market.index.emptyNormal')}
            </Text>
          </View>
        )}

        {/* ── Liste des produits (recherche ou feed normal) ── */}
        {!displayedLoading && displayedProducts.map(renderProductCard)}

        {/* ── Pagination en cours ── */}
        {displayedLoadingMore && (
          <View style={styles.loadMoreBox}>
            <ActivityIndicator size="small" color="#6610B1" />
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0F2F5',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingTop: 6,
    // Pas de justifyContent: 'center' ici — c'était la cause d'un bug
    // précédent : avec un contenu court, ça centrait le peu de contenu
    // au lieu de le laisser s'empiler depuis le haut, masquant le fait
    // qu'il n'y avait jamais assez de hauteur pour scroller.
  },
  headerBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
    marginBottom: 12,
  },
  headerCount: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#666',
    flexShrink: 0,
  },
  headerSearchWrap: {
    flex: 1,
  },
  categoryFilterWrap: {
    marginBottom: 16,
  },
  centerBox: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
    gap: 10,
  },
  centerBoxText: {
    fontSize: 13,
    color: '#999',
    textAlign: 'center',
  },
  retryBtn: {
    marginTop: 4,
    paddingHorizontal: 18,
    paddingVertical: 9,
    backgroundColor: '#F3E8FF',
    borderRadius: 10,
  },
  retryBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6610B1',
  },
  loadMoreBox: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  productCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 12,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  productTopRow: {
    flexDirection: 'row',
  },
  productImageWrap: {
    width: 78,
    height: 78,
    borderRadius: 10,
    marginRight: 12,
    position: 'relative',
    overflow: 'hidden',
  },
  productImage: {
    width: '100%',
    height: '100%',
    backgroundColor: '#F3E8FF',
  },
  productImagePlaceholder: {
    width: 78,
    height: 78,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 4,
    left: 4,
    backgroundColor: '#6610B1',
    borderRadius: 6,
    paddingHorizontal: 5,
    paddingVertical: 2,
  },
  featuredBadgeText: {
    fontSize: 8,
    fontWeight: '700',
    color: '#fff',
  },
  favoriteBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  productInfo: {
    flex: 1,
  },
  productName: {
    fontSize: 14.5,
    fontWeight: '700',
    color: '#222',
  },
  productShop: {
    fontSize: 12,
    color: '#999',
    marginTop: 1,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
    marginTop: 5,
  },
  price: {
    fontSize: 14,
    fontWeight: '700',
    color: '#6610B1',
  },
  promoPrice: {
    fontSize: 14,
    fontWeight: '700',
    color: '#6610B1',
  },
  originalPrice: {
    fontSize: 12,
    color: '#999',
    textDecorationLine: 'line-through',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: '#F9F5FF',
    paddingHorizontal: 8,
    paddingVertical: 7,
    borderRadius: 8,
  },
  actionBtnSecondary: {
    backgroundColor: '#F3F0FF',
  },
  actionBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
  },
});
